import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlinePencilSquare } from 'react-icons/hi2';
import { CHAT_HEADER_BUTTON, ChatPanelShell, type ChatComposerHandle } from '@reloam/ui';
import { useChat } from '../../contexts/ChatContext';
import useContactStore from '../../stores/contactStore';
import useMessageStore from '../../stores/messageStore';
import { useUserStore } from '../../stores/userStore';
import AssistantThread from '../Assistant/AssistantThread';
import { useAssistantStore } from '../Assistant/assistantStore';
import ChannelActions from './ChannelActions';
import ContactSearch from './ContactSearch';
import ConversationSwitcher, { ChannelAvatar } from './ConversationSwitcher';
import PeerThread from './PeerThread';
import {
  ASSISTANT_PEER_ID,
  buildSwitcherRows,
  channelKindFor,
  contactIdFor,
  plainPreview,
} from './conversationOrder';
import { usePeerSenders } from './usePeerSenders';
import { useTenantMessageSync } from './useTenantMessageSync';

/**
 * The single chat panel: one inbox for the AI assistant and every person
 * thread (tenants, agents / service desk, SMS, WhatsApp). It always opens on
 * the thread chosen by ChatContext (the Assistant by default); the switcher is
 * a left column on desktop and a list screen with a back button on phones.
 */
export default function ChatPanel() {
  const {
    activePeerId,
    chatType,
    conversations,
    messages,
    peers,
    closeChat,
    switchToConversation,
    addTenantConversation,
    addMessage,
  } = useChat();
  const contacts = useContactStore((s) => s.contacts);
  const broadcastMessages = useMessageStore((s) => s.broadcastMessages);
  const currentUser = useUserStore((s) => s.currentUser);
  const assistantMessages = useAssistantStore((s) => s.messages);
  const clearAssistant = useAssistantStore((s) => s.clear);

  const [view, setView] = useState<'list' | 'thread'>('thread');
  const [searching, setSearching] = useState(false);
  const [draft, setDraft] = useState('');
  const composerRef = useRef<ChatComposerHandle>(null);

  const { handleRefresh, isRefreshing, createdConversationsRef } = useTenantMessageSync(true);

  const peerId = activePeerId ?? ASSISTANT_PEER_ID;
  const kind = channelKindFor(peerId);
  const contactId = contactIdFor(peerId);
  const contact = contactId ? contacts.find((c) => String(c.id) === contactId) : undefined;
  const contactName = contact ? `${contact.first_name} ${contact.last_name}`.trim() : undefined;
  const peerName = kind === 'assistant' ? 'Re:Loam Assistant' : contactName || peers[peerId]?.name || 'Conversation';
  const phone = contact?.phone || undefined;

  const senders = usePeerSenders({
    chatType,
    tenantId: contactId ?? undefined,
    tenantName: peerName,
    tenantPhone: phone,
    createdConversationsRef,
  });

  const lastAssistant = assistantMessages[assistantMessages.length - 1];
  const contactNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of contacts) map.set(String(c.id), `${c.first_name} ${c.last_name}`.trim());
    return map;
  }, [contacts]);
  // A tenant chat created from an incoming message is first named "Tenant 5";
  // show the contact's real name as soon as we know it.
  const rows = useMemo(
    () =>
      buildSwitcherRows(conversations, peerId, {
        preview: plainPreview(lastAssistant?.content),
        time: lastAssistant?.createdAt,
      }).map((r) => {
        const id = r.kind === 'tenant' ? contactIdFor(r.id) : null;
        const name = id ? contactNames.get(id) : undefined;
        return name ? { ...r, name } : r;
      }),
    [conversations, peerId, lastAssistant, contactNames],
  );

  // Whenever the active conversation changes (switcher, another page's
  // "message" button, the user menu) go straight to its thread.
  useEffect(() => {
    setView('thread');
    setSearching(false);
    setDraft('');
    composerRef.current?.focus();
  }, [peerId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (searching) setSearching(false);
      else closeChat();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeChat, searching]);

  const handleSelect = useCallback(
    (id: string) => {
      switchToConversation(id);
      setView('thread');
    },
    [switchToConversation],
  );

  const handlePickContact = useCallback(
    (id: string, name: string) => {
      const key = `tenant:${id}`;
      if (!createdConversationsRef.current.has(key)) {
        addTenantConversation(id, name);
        createdConversationsRef.current.add(key);
      }
      setSearching(false);
      handleSelect(key);
    },
    [addTenantConversation, createdConversationsRef, handleSelect],
  );

  const handlePeerSend = useCallback(
    (text: string) => {
      if (kind === 'tenant') void senders.handleTenantMessage(text);
      else if (kind === 'sms') void senders.handleSMSMessage(text);
      else if (kind === 'whatsapp') void senders.handleWhatsAppMessage(text);
      else if (kind === 'service_desk') addMessage({ role: 'user', content: text.trim() }, peerId);
    },
    [kind, senders, addMessage, peerId],
  );

  const senderName = useCallback(
    (senderId: string) => {
      const c = contacts.find((x) => String(x.id) === senderId);
      const name = c ? `${c.first_name} ${c.last_name}`.trim() : '';
      return name || `Tenant ${senderId}`;
    },
    [contacts],
  );

  const subtitle =
    kind === 'assistant'
      ? 'Answers from your plots, leases and invoices'
      : kind === 'sms'
        ? phone ? `Send SMS to ${phone}` : 'SMS messaging'
        : kind === 'whatsapp'
          ? phone ? `Send WhatsApp to ${phone}` : 'WhatsApp messaging'
          : kind === 'service_desk'
            ? 'Shared with your team'
            : 'Direct message';
  const needsPhone = (kind === 'sms' || kind === 'whatsapp') && !phone;

  return (
    <ChatPanelShell
      ariaLabel="Messages and assistant"
      view={view}
      onClose={closeChat}
      onBack={() => setView('list')}
      sidebar={
        <>
          <ConversationSwitcher
            rows={rows}
            onSelect={handleSelect}
            onNew={() => setSearching(true)}
            onRefresh={() => void handleRefresh()}
            refreshing={isRefreshing}
          />
          {searching && <ContactSearch onPick={handlePickContact} onClose={() => setSearching(false)} />}
        </>
      }
      avatar={<ChannelAvatar kind={kind} onPrimary />}
      title={peerName}
      subtitle={subtitle}
      headerActions={
        <>
          {kind !== 'assistant' && <ChannelActions phone={phone} draft={draft} />}
          {kind === 'assistant' && assistantMessages.length > 0 && (
            <button type="button" onClick={clearAssistant} className={CHAT_HEADER_BUTTON} aria-label="New chat" title="New chat">
              <HiOutlinePencilSquare className="h-5 w-5" aria-hidden />
            </button>
          )}
        </>
      }
    >
      {kind === 'assistant' ? (
        <AssistantThread composerRef={composerRef} onNavigated={() => window.matchMedia?.('(max-width: 767px)').matches && closeChat()} />
      ) : (
        <PeerThread
          key={peerId}
          kind={kind}
          peerName={peerName}
          messages={messages}
          broadcastMessages={broadcastMessages as never}
          currentUserId={currentUser?.id}
          senderName={senderName}
          isSending={senders.isSending}
          lockedReason={needsPhone ? 'No phone number on file for this contact' : undefined}
          onSend={handlePeerSend}
          composerRef={composerRef}
          onDraftChange={setDraft}
        />
      )}
    </ChatPanelShell>
  );
}
